import { environment } from '../../../environments/environment';

type GoogleMapsWindow = Window & { google?: { maps?: { places?: unknown } } };

let loadPromise: Promise<GoogleMapsWindow['google']> | null = null;

function isGoogleMapsApiKeyConfigured(): boolean {
  const key = environment.googleSearchApiKey;
  return !!key && !key.startsWith('__');
}

function waitForGoogle(
  resolve: (google: GoogleMapsWindow['google']) => void,
  reject: (reason?: unknown) => void,
  attempts = 100
): void {
  const googleApi = (window as GoogleMapsWindow).google;
  if (googleApi?.maps?.places) {
    resolve(googleApi);
    return;
  }
  if (attempts <= 0) {
    loadPromise = null;
    reject(new Error('Google Maps API timed out'));
    return;
  }
  setTimeout(() => waitForGoogle(resolve, reject, attempts - 1), 100);
}

export function loadGoogleMapsPlaces(): Promise<GoogleMapsWindow['google']> {
  const win = window as GoogleMapsWindow;
  if (win.google?.maps?.places) {
    return Promise.resolve(win.google);
  }

  if (!isGoogleMapsApiKeyConfigured()) {
    return Promise.reject(
      new Error('Google Maps API key is not configured (googleSearchApiKey)')
    );
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const onReady = () => waitForGoogle(resolve, reject);
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    if (existing) {
      if (win.google?.maps?.places) {
        resolve(win.google);
        return;
      }
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener(
        'error',
        () => {
          loadPromise = null;
          reject(new Error('Google Maps API failed to load'));
        },
        { once: true }
      );
      waitForGoogle(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleSearchApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Google Maps API failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
